import { useEffect, useRef } from 'react';
import { Button } from '@/components/button';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';
import styles from './sheet.module.css';
import clsx from 'clsx';
import { useTranslation } from '@/contexts/i18n-context';

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  onClear?: () => void;
  hasCloseButton?: boolean;
  children: React.ReactNode;
  title: string;
  titleClassName?: string;
}

export function Sheet({
  isOpen,
  onClose,
  onClear,
  hasCloseButton = true,
  children,
  title,
  titleClassName,
}: SheetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const handleOnClear = () => {
    onClear?.();
    onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div className={clsx(styles.container, isOpen && styles.open)}>
      <div ref={ref} className={clsx(styles.sheet, isOpen && styles.open)}>
        <div className={styles.header}>
          <div className={clsx(styles.title, titleClassName)}>
            {hasCloseButton && (
              <MaterialSymbolIcon name="close" onClick={onClose} ariaLabel="Close" />
            )}
            <span className={clsx('h4')}>{title}</span>
          </div>
          {onClear && (
            <Button variant="textOnly" onClick={handleOnClear} className={styles.clearButton}>
              {t('common.clearAll')}
            </Button>
          )}
        </div>

        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
}
